/**
 * hydrateCandidateGrids — READ-TIME grid hydration for L1A candidate properties.
 *
 * THE BUG IT FIXES (AD-013 / L1A grid-reflection):
 *   The grid (boundary + gridId) is a PROPERTY-level asset traced ONCE on the
 *   Property page (`properties/{pid}` → active concept ▸ version ▸ floor). The
 *   project, however, reads grid STATUS off each candidate's own
 *   `answers.properties[i].floors`, which the intake write side never populates
 *   with a boundary. So a property can be fully gridded while the project still
 *   reports "0 floors · NOT STARTED" and the report gate never opens.
 *
 * THE FIX (contained, at the data-loading layer — pure derivers untouched):
 *   Before the candidate list reaches the pure derivers (`deriveL1AStepStates`,
 *   `gridSummary`, `candidateGridStatus`), look up each candidate's LINKED
 *   registry property, resolve its ACTIVE grid via the existing pure
 *   `resolvePropertyGridForMarking`, and MERGE that boundary (+ gridId FK) onto
 *   the candidate's floor. Boundary is read-time/property-level; ENTRANCE/AUO
 *   marks stay engagement-level and are NEVER clobbered.
 *
 * SPLIT: the pure `mergePropertyGridOntoCandidate` carries all the merge logic
 * (no Firebase, unit-testable directly); `hydrateCandidatesWithPropertyGrids` is
 * the thin async wrapper that does the (deduped, parallel, failure-tolerant)
 * Firebase property reads and delegates each merge to the pure function.
 *
 * NO WRITES — this is read-time hydration only. The hydrated array feeds status
 * derivation / display; it is never fed back to a save path.
 */
import { blankFloor, type FloorInput, type PropertyInput } from './staffIntake';
import { floorHasBoundary } from './candidateGrid';
import { isValidBoundary } from './vastuGrid';
import {
  resolvePropertyGridForMarking,
  type PropertyGridForMarking,
} from './propertyGridForMarking';
import { getProperty } from './propertyStore';

/** The slice of a resolved property grid the merge needs: the traced boundary +
 *  its grid FK. Satisfied by the full `PropertyGridForMarking`. */
export type ResolvedPropertyGrid = Pick<PropertyGridForMarking, 'boundary' | 'gridId'>;

/**
 * PURE — merge a resolved property grid onto ONE candidate's floor.
 *
 *   · `resolved` null / no usable boundary → candidate returned UNCHANGED
 *     (a candidate without a gridded property correctly stays "not started").
 *   · candidate has NO floors → synthesise a single floor carrying the boundary
 *     (+ gridId), matching the canonical `FloorInput` shape (`blankFloor`).
 *   · candidate's first floor has NO boundary → add boundary (+ gridId), and
 *     PRESERVE every existing entrance/door/fixture mark (engagement-level marks
 *     are candidate-owned — never clobbered).
 *   · candidate's first floor ALREADY has a boundary → left untouched (the
 *     candidate's own trace wins; never clobbered).
 *
 * Immutable: returns a new candidate (and floors array) when it changes; returns
 * the SAME reference unchanged otherwise. Never mutates the input.
 */
export function mergePropertyGridOntoCandidate(
  candidate: PropertyInput,
  resolved: ResolvedPropertyGrid | null | undefined,
): PropertyInput {
  // No grid resolved (no property, property ungridded, or read failed) → unchanged.
  if (!resolved || !isValidBoundary(resolved.boundary)) return candidate;
  // Pt2D[] (property model) and Pt[] (grid lib) are the identical {x,y} shape;
  // the guard above narrowed it to a valid ≥3-pt boundary.
  const boundary: FloorInput['boundary'] = resolved.boundary;
  const gridId = resolved.gridId ?? undefined;
  const floors = Array.isArray(candidate.floors) ? candidate.floors : [];

  // No floors at all → one fresh floor carrying the property's boundary (+ gridId).
  if (floors.length === 0) {
    const floor: FloorInput = {
      ...blankFloor(),
      boundary,
      ...(gridId ? { gridId } : {}),
    };
    return { ...candidate, floors: [floor] };
  }

  const first = floors[0];
  // Candidate already gridded on its own side → never clobber boundary OR marks.
  if (floorHasBoundary(first)) return candidate;

  // Floor exists but has no boundary → add boundary (+ gridId); keep every mark.
  // 2.1 (comparative shows grid + entrances + AUO): the entrance `doors[]` and AUO
  // `fixtures[]` are ENGAGEMENT-level marks that ride on THIS candidate floor (they
  // are marked at the engagement, never on the property — see propertyGridForMarking).
  // Carry them through hydration EXPLICITLY so the comparative read-only preview can
  // render boundary + entrances + AUO off a single, guaranteed-shaped floor.
  const merged: FloorInput = {
    ...first,
    boundary,
    ...(gridId ? { gridId } : {}),
    doors: Array.isArray(first.doors) ? first.doors : [],
    fixtures: Array.isArray(first.fixtures) ? first.fixtures : [],
  };
  const nextFloors = floors.slice();
  nextFloors[0] = merged;
  return { ...candidate, floors: nextFloors };
}

/**
 * Hydrate a candidate list with each candidate's LINKED property grid (read-time).
 *
 *   · Dedupes property reads by `propertyId`; runs them in PARALLEL (`Promise.all`).
 *   · A property-read failure (or an ungridded property) is NON-FATAL → that
 *     candidate passes through unhydrated.
 *   · Immutable — returns a brand-new array; inputs are never mutated.
 */
export async function hydrateCandidatesWithPropertyGrids(
  candidates: PropertyInput[],
): Promise<PropertyInput[]> {
  const list = Array.isArray(candidates) ? candidates : [];

  // Dedupe the linked-property reads.
  const ids = Array.from(
    new Set(
      list
        .map((c) => (typeof c.propertyId === 'string' ? c.propertyId.trim() : ''))
        .filter((id) => id.length > 0),
    ),
  );
  // Nothing to hydrate — still return a NEW array (immutable contract).
  if (ids.length === 0) return list.slice();

  // Resolve every linked property's active grid in parallel; failures → null.
  const resolvedById = new Map<string, ResolvedPropertyGrid | null>();
  await Promise.all(
    ids.map(async (pid) => {
      try {
        const prop = await getProperty(pid);
        resolvedById.set(pid, resolvePropertyGridForMarking(prop));
      } catch {
        resolvedById.set(pid, null);
      }
    }),
  );

  return list.map((c) => {
    const pid = typeof c.propertyId === 'string' ? c.propertyId.trim() : '';
    if (!pid) return c;
    return mergePropertyGridOntoCandidate(c, resolvedById.get(pid) ?? null);
  });
}
